"use client"

import { useState, useRef, useEffect } from 'react'
import { lazy, Suspense } from 'react'
import * as stylex from '@stylexjs/stylex'
import { useMediaContext } from '@/context/MediaContext'
import { VideoGalleryItem } from '@/models/gallery'
import { videoPageStyles } from '@/styles/VideoPage.stylex'

const VideoPopupRaw = lazy(() => import('@/components/popup/VideoPopup'));
const VideoPopup = (props: any) => (
    <Suspense fallback={null}>
        <VideoPopupRaw {...props} />
    </Suspense>
);

interface AudioVideoToVideoGalleryProps {
    title: string
    description: string
    modelKey: string
    modelLabel: string
    modelNote: string
    allVideos: string[]
    allAudios: string[]
    items: VideoGalleryItem[]
}

export function AudioVideoToVideoGallery({ title, description, modelKey, modelLabel, modelNote, items, allVideos, allAudios }: AudioVideoToVideoGalleryProps) {
    const [showPopup, setShowPopup] = useState(false)
    const [selectedItem, setSelectedItem] = useState<VideoGalleryItem>({ id: '', videoSrc: '', className: '', prompt: '', aspect_ratio: '', referenceVideos: [], referenceAudios: [] })
    const { registerMediaElement, unregisterMediaElement, onMediaPlay } = useMediaContext()
    const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map())
    const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map())

    // Register all media elements with MediaContext
    useEffect(() => {
        videoRefs.current.forEach((video, id) => {
            registerMediaElement(id, video)
        })
        audioRefs.current.forEach((audio, id) => {
            registerMediaElement(id, audio)
        })
        return () => {
            videoRefs.current.forEach((_, id) => {
                unregisterMediaElement(id)
            })
            audioRefs.current.forEach((_, id) => {
                unregisterMediaElement(id)
            })
        }
    }, [registerMediaElement, unregisterMediaElement, items])

    const handleVideoHover = (videoElement: HTMLVideoElement, videoId: string, isEntering: boolean) => {
        if (isEntering) {
            onMediaPlay(videoId)
            videoElement.muted = false
            videoElement.play().catch((error) => {
                if (error.name === 'AbortError') return
                videoElement.muted = true
                videoElement.play().catch(() => { })
            })
        } else {
            videoElement.pause()
            videoElement.muted = true
        }
    }

    const openPopup = (item: VideoGalleryItem) => {
        setSelectedItem(item)
        setShowPopup(true)
    }

    return (
        <>
            <div {...stylex.props(videoPageStyles.gallerySection)}>
                <h3>{title}</h3>
                <p dangerouslySetInnerHTML={{ __html: description }} />
                <div {...stylex.props(videoPageStyles.textImageVideoList)}>
                    {items.map((item, index) => (
                        <GalleryItem
                            key={item.id || index}
                            item={item}
                            index={index}
                            videoRefs={videoRefs}
                            audioRefs={audioRefs}
                            onHover={handleVideoHover}
                            onPlay={onMediaPlay}
                            onClick={() => openPopup(item)}
                        />
                    ))}
                </div>
            </div>

            <VideoPopup
                isOpen={showPopup}
                onClose={() => setShowPopup(false)}
                id={selectedItem.id}
                videoSrc={selectedItem.videoSrc}
                thumbnailSrc={selectedItem.thumbnailSrc}
                modelKey={modelKey}
                modelLabel={modelLabel}
                prompt={selectedItem.prompt}
                aspect_ratio={selectedItem.aspect_ratio}
                popupTitle="Video Details"
                modelNote={modelNote}
                allVideos={allVideos}
                allAudios={allAudios}
                selectedReferenceImages={[]}
                selectedReferenceVideos={selectedItem.referenceVideos || []}
                selectedReferenceAudios={selectedItem.referenceAudios || []}
            />
        </>
    )
}

/**
 * Internal sub-component to handle per-item hover states for StyleX
 */
function GalleryItem({ item, index, videoRefs, audioRefs, onHover, onPlay, onClick }: any) {
    const [isHovered, setIsHovered] = useState(false);
    const refVideoId = `audio-video-ref-${item.id || index}`
    const genVideoId = `audio-video-gen-${item.id || index}`
    const audioId = `audio-video-audio-${item.id || index}`

    return (
        <div
            {...stylex.props(videoPageStyles.textImageVideoItem)}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div {...stylex.props(videoPageStyles.imageVideoPair)}>
                <div {...stylex.props(videoPageStyles.refVideo)}>
                    <video
                        ref={(el) => { if (el) videoRefs.current.set(refVideoId, el) }}
                        src={item.referenceVideos?.[0]}
                        {...stylex.props(videoPageStyles.videoElement)}
                        loop
                        muted
                        playsInline
                        onMouseEnter={(e) => onHover(e.target as HTMLVideoElement, refVideoId, true)}
                        onMouseLeave={(e) => onHover(e.target as HTMLVideoElement, refVideoId, false)}
                    />
                    <div {...stylex.props(videoPageStyles.refAudio)}>
                        <audio
                            ref={(el) => { if (el) audioRefs.current.set(audioId, el) }}
                            src={item.referenceAudios?.[0]}
                            {...stylex.props(videoPageStyles.audioElement)}
                            controls
                            preload="none"
                            onPlay={() => onPlay(audioId)}
                        />
                    </div>
                </div>

                <div {...stylex.props(videoPageStyles.arrowSection)}>
                    <div {...stylex.props(
                        videoPageStyles.arrow,
                        isHovered && videoPageStyles.arrowHover
                    )}>→</div>
                </div>

                <div {...stylex.props(videoPageStyles.generatedVideo)}>
                    <div {...stylex.props(videoPageStyles.videoContainer)}>
                        <video
                            ref={(el) => { if (el) videoRefs.current.set(genVideoId, el) }}
                            src={item.videoSrc}
                            poster={item.thumbnailSrc}
                            {...stylex.props(videoPageStyles.comparisonVideo)}
                            loop
                            muted
                            preload="none"
                            playsInline
                            onMouseEnter={(e) => onHover(e.target as HTMLVideoElement, genVideoId, true)}
                            onMouseLeave={(e) => onHover(e.target as HTMLVideoElement, genVideoId, false)}
                        />
                    </div>
                </div>
            </div>
            <div {...stylex.props(
                videoPageStyles.clickIndicatorCenter,
                isHovered && videoPageStyles.indicatorActive
            )}>
                Click me
            </div>
        </div>
    );
}